import { tokenStore } from "./token-store";

/**
 * The one place this application talks to the network.
 *
 * Everything reaches ms-xpsims-gateway on a single origin, so this wrapper is
 * deliberately thin. It owns exactly four concerns, and nothing else should
 * acquire them:
 *
 *   1. attaching the access token
 *   2. refreshing once on a 401, and only once
 *   3. carrying the correlation id back out of failures
 *   4. turning every failure into one shape
 *
 * Business logic does not live here, and neither does retry-on-5xx: TanStack
 * Query already owns retry policy, and having two layers retry independently
 * turns one slow request into four.
 */

/** Where the gateway is. Same-origin in development via the Vite proxy. */
const BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

/**
 * A failed request, in the one shape the UI handles.
 *
 * The correlation id matters more than it looks. Every XPSIMS service logs
 * against it, so a user reporting "it failed at 2pm" is unactionable while a
 * user reporting a correlation id is a single log query. It is surfaced in
 * error toasts for that reason.
 */
export class ApiError extends Error {
    public readonly status: number;
    /**
     * The machine-readable code from `{ error: { code } }`, when one was sent.
     *
     * Read it only where the status is genuinely ambiguous — a 403 that means
     * "finish signing in" has to be told apart from a 403 that means "not
     * yours", and no status code distinguishes them. Everywhere else the
     * status and the message are the better things to react to, because they
     * are what the user is shown.
     */
    public readonly code?: string;
    public readonly correlationId?: string;
    public readonly details?: unknown;

    public constructor(
        message: string,
        status: number,
        correlationId?: string,
        details?: unknown,
        code?: string
    ) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.correlationId = correlationId;
        this.details = details;
        this.code = code;
    }

    /**
     * Whether retrying could plausibly succeed.
     *
     * A 4xx will fail again with the same body; a 5xx or a network drop might
     * not. TanStack Query reads this rather than guessing from the message.
     */
    public get isRetryable(): boolean {
        return this.status === 0 || this.status >= 500;
    }
}

/**
 * The in-flight refresh, if there is one.
 *
 * Without this, a screen that fires six queries on mount and finds the token
 * expired sends six refresh requests. Five of them then present a rotated —
 * and therefore invalid — refresh token and log the user out, which reads to
 * the user as "it signs me out at random". Sharing one promise makes the six
 * callers wait on a single refresh.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the refresh token for a new access token.
 *
 * @returns Whether the session survived
 */
async function refreshSession(): Promise<boolean> {
    const refreshToken = tokenStore.getRefreshToken();

    if (!refreshToken) {
        return false;
    }

    try {
        const response = await fetch(`${BASE_URL}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
            credentials: "include",
        });

        if (!response.ok) {
            return false;
        }

        const body = (await response.json()) as {
            accessToken?: string;
            refreshToken?: string;
        };

        if (!body.accessToken) {
            return false;
        }

        tokenStore.set(body.accessToken, body.refreshToken ?? refreshToken);
        return true;
    } catch {
        // A network failure during refresh is not proof the session is over —
        // but there is nothing to proceed with either, so the caller surfaces
        // the original 401. The session is cleared by the caller, not here.
        return false;
    }
}

/**
 * Forces a token refresh outside the 401 retry path.
 *
 * There is exactly one caller and it is not an error path: after a password
 * change, the access token still carries `mustChangePassword` and the gateway
 * is still refusing on the strength of it, while the session behind it has
 * already been cleared. Nothing 401s, so the automatic refresh never fires, and
 * the user would sit on the change-password screen for an access-token lifetime
 * having already done what was asked.
 *
 * @returns Whether the session survived
 */
export async function refreshAccessToken(): Promise<boolean> {
    // Through the same shared promise as the automatic path, so this cannot
    // race a refresh already in flight and invalidate its rotated token.
    refreshInFlight ??= refreshSession().finally(() => {
        refreshInFlight = null;
    });

    return refreshInFlight;
}

/** Options accepted by {@link request}, minus the ones this wrapper owns. */
export interface RequestOptions extends Omit<RequestInit, "body"> {
    /** Serialised as JSON unless it is already a FormData (file uploads). */
    body?: unknown;
    /** Appended as a query string; `undefined` and `null` entries are dropped. */
    query?: Record<string, string | number | boolean | undefined | null>;
    /** Skips the token and the refresh dance — for login and forgot-password. */
    anonymous?: boolean;
    /**
     * Read the body as a Blob instead of JSON — for images, which an <img>
     * cannot fetch itself because it cannot send the bearer token.
     */
    responseAs?: "blob";
}

/**
 * Builds a query string, omitting empty values.
 *
 * Dropping `undefined` rather than serialising it is what lets callers spread
 * optional filters into one object without each call site guarding every field.
 * A literal `?status=undefined` reaching a service is a filter that matches
 * nothing, which is the confusing kind of wrong.
 *
 * @param query - The parameters to encode
 * @returns The query string including its `?`, or an empty string
 */
function buildQuery(query: RequestOptions["query"]): string {
    if (!query) {
        return "";
    }

    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== "") {
            params.append(key, String(value));
        }
    }

    const serialised = params.toString();
    return serialised ? `?${serialised}` : "";
}

/**
 * Turns a failed response into an {@link ApiError}.
 *
 * @param response - The failed response
 * @returns The error to throw
 */
/**
 * The per-field reasons in a validation failure, in whichever shape they came.
 *
 * The error middleware flattens them to "property: reason" strings under
 * `details.errors`; routing-controllers' own failure carries class-validator
 * objects (`{ property, constraints }`) under `errors`. Both are read.
 *
 * @param sources - Candidate containers from the response body
 * @returns The reasons, e.g. ["phone: phone must be longer than or equal to 7 characters"]
 */
function fieldReasons(...sources: unknown[]): string[] {
    const reasons: string[] = [];
    const visit = (item: unknown, path: string): void => {
        if (typeof item === "string") {
            reasons.push(item);
            return;
        }
        if (!item || typeof item !== "object") {
            return;
        }
        const entry = item as { property?: string; constraints?: Record<string, string>; children?: unknown[] };
        const here = entry.property ? (path ? `${path}.${entry.property}` : entry.property) : path;
        for (const constraint of Object.values(entry.constraints ?? {})) {
            reasons.push(here ? `${here}: ${constraint}` : constraint);
        }
        for (const child of entry.children ?? []) {
            visit(child, here);
        }
    };
    for (const source of sources) {
        const list = Array.isArray(source)
            ? source
            : Array.isArray((source as { errors?: unknown } | undefined)?.errors)
              ? (source as { errors: unknown[] }).errors
              : [];
        for (const item of list) {
            visit(item, "");
        }
    }
    return reasons;
}

async function toApiError(response: Response): Promise<ApiError> {
    const correlationId = response.headers.get("x-correlation-id") ?? undefined;

    let message = response.statusText || `Request failed (${response.status})`;
    let details: unknown;
    let code: string | undefined;

    try {
        const body = (await response.json()) as {
            message?: string;
            error?: string | { code?: string; message?: string; details?: unknown };
            details?: unknown;
        };

        // Every XPSIMS service answers `{ error: { code, message, details? } }`
        // — that is what `errorHandlerMiddleware` in @exp-node/error-handling
        // writes, for domain errors, for routing-controllers validation
        // failures and for unexpected 500s alike.
        //
        // This used to read `body.message ?? body.error`, which is the shape
        // nothing here actually sends. `body.error` is an OBJECT, so the
        // nullish check passed, the object went into `new Error(...)`, and
        // every failed request in the whole application reported itself as:
        //
        //     Something went wrong
        //     [object Object]
        //
        // Nothing threw and nothing logged, so it read as a mystery rather
        // than as a bug — and it wiped out exactly the sentence that says
        // which field was rejected or which record already exists. The flat
        // forms are still accepted: the gateway's own error paths and any
        // proxy in front of it are not bound by this service convention.
        const nested = typeof body.error === "object" && body.error !== null ? body.error : undefined;
        const flat = typeof body.error === "string" ? body.error : undefined;

        message = body.message ?? nested?.message ?? flat ?? message;
        details = body.details ?? nested?.details;
        code = nested?.code;

        // A validation failure's message is generic ("Invalid body, check
        // 'errors' property for more info") and says nothing a person can
        // act on. The field reasons beside it do, so they become the message.
        const reasons = fieldReasons(details, (body as { errors?: unknown }).errors);
        if (reasons.length > 0) {
            const shown = reasons.slice(0, 3).join("; ");
            message = reasons.length > 3 ? `${shown} (and ${reasons.length - 3} more)` : shown;
        }
    } catch {
        // A body that is not JSON — an nginx error page, a gateway timeout —
        // leaves the status line as the best description available.
    }

    return new ApiError(message, response.status, correlationId, details, code);
}

/**
 * Performs one request against the gateway.
 *
 * @param path - Path below the gateway prefix, e.g. `/students/abc`
 * @param options - Body, query, and any fetch options
 * @param isRetry - Internal: set when this is the post-refresh second attempt
 * @returns The parsed response body, or undefined for a 204
 * @throws {ApiError} On any non-2xx response or network failure
 */
export async function request<T>(
    path: string,
    options: RequestOptions = {},
    isRetry = false
): Promise<T> {
    const { body, query, anonymous, headers, responseAs, ...rest } = options;

    const isFormData = body instanceof FormData;
    const token = anonymous ? undefined : tokenStore.getAccessToken();

    const requestHeaders = new Headers(headers);

    // Set by fetch itself for FormData, complete with the multipart boundary.
    // Setting it by hand produces a boundary-less header and an unparseable
    // upload, which is a genuinely hard failure to read from the server side.
    if (!isFormData && body !== undefined) {
        requestHeaders.set("Content-Type", "application/json");
    }

    if (token) {
        requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    let response: Response;

    try {
        response = await fetch(`${BASE_URL}${path}${buildQuery(query)}`, {
            ...rest,
            headers: requestHeaders,
            body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
            credentials: "include",
        });
    } catch (error) {
        // Status 0 — the request never reached the gateway. Distinguished from
        // a 502, which means it did and the gateway could not reach a service.
        throw new ApiError(
            error instanceof Error ? error.message : "Network request failed",
            0
        );
    }

    if (response.status === 401 && !anonymous && !isRetry) {
        const recovered = await (refreshInFlight ??= refreshSession().finally(() => {
            refreshInFlight = null;
        }));

        if (recovered) {
            return request<T>(path, options, true);
        }

        // One attempt, then out. Clearing here rather than redirecting keeps
        // this module free of routing: the auth context observes the empty
        // store and moves the user, so a background poll failing at 3am does
        // not navigate a page the user is not looking at.
        tokenStore.clear();
    }

    if (!response.ok) {
        throw await toApiError(response);
    }

    if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined as T;
    }

    if (responseAs === "blob") {
        return (await response.blob()) as T;
    }

    return (await response.json()) as T;
}

/** Verb helpers. Thin on purpose — they exist so call sites read as sentences. */
export const api = {
    get: <T>(path: string, options?: RequestOptions) =>
        request<T>(path, { ...options, method: "GET" }),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
        request<T>(path, { ...options, method: "POST", body }),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
        request<T>(path, { ...options, method: "PUT", body }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
        request<T>(path, { ...options, method: "PATCH", body }),
    delete: <T>(path: string, options?: RequestOptions) =>
        request<T>(path, { ...options, method: "DELETE" }),
};
